import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from '../../lib/apiClient';

/**
 * The set of AI output types the feedback control currently supports on the
 * frontend. This mirrors the backend's allow-list (see
 * src/constants/aiOutputTypes.ts) but intentionally omits
 * 'zeovit_dosing_recommendation' — that surface is not yet wired up per the
 * epic's scope (#1435 digest: "Mount under ReefInsightCard, journal AI
 * observation, image insight, AI task suggestion displays (NOT ZeoVit yet)").
 */
export type AiFeedbackOutputType =
  | 'reef_intelligence_card'
  | 'journal_ai_observation'
  | 'image_insight'
  | 'ai_task_suggestion'
  | 'zeovit_dosing_recommendation'
  // Voice notes (docs/voiceFeature.md P2): one rating per capture on the review card.
  | 'voice_capture';

/** Single nullable rating value — structurally mutually exclusive by design. */
export type AiFeedbackRating = 'up' | 'down' | null;

/** One feedback record as the API returns it (camelCase, inside `{ data }`). */
interface AiFeedbackApiRecord {
  id: string;
  aquariumId: string;
  outputType: AiFeedbackOutputType;
  outputId: string;
  rating: 1 | -1;
  comment: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AiFeedbackEnvelope {
  data: AiFeedbackApiRecord | null;
}

interface UpsertAiFeedbackPayload {
  aquariumId: string;
  outputType: AiFeedbackOutputType;
  outputId: string;
  rating: 1 | -1;
  comment?: string | null;
}

function ratingToApi(rating: Exclude<AiFeedbackRating, null>): 1 | -1 {
  return rating === 'up' ? 1 : -1;
}

function ratingFromApi(rating: 1 | -1 | null | undefined): AiFeedbackRating {
  if (rating === 1) return 'up';
  if (rating === -1) return 'down';
  return null;
}

export interface UseAiFeedbackArgs {
  outputType: AiFeedbackOutputType;
  outputId: string;
  aquariumId: string;
}

export interface UseAiFeedbackResult {
  /** Current persisted-or-optimistic rating. */
  rating: AiFeedbackRating;
  /** Current comment text (editable, independent of the persisted value). */
  comment: string;
  /** Setter for the comment text box — does not persist until submitComment is called. */
  setComment: (value: string) => void;
  /** Whether the initial hydration GET is in flight. */
  isLoading: boolean;
  /** Whether a rating mutation (thumbs click) is in flight. */
  isRatingSubmitting: boolean;
  /** Whether a comment mutation (save click) is in flight. */
  isCommentSubmitting: boolean;
  /** Whether any mutation (rating or comment) is in flight. */
  isMutating: boolean;
  /** Whether the most recent mutation ended in an error. */
  isError: boolean;
  /** Error from the most recent failed mutation, if any. */
  error: Error | null;
  /** True once a rating exists (required before a comment can be submitted). */
  hasRating: boolean;
  /** True if the comment textarea content differs from the last persisted comment. */
  isCommentDirty: boolean;
  /** Click handler for a thumb button — toggles off if the same rating is clicked again. Resolves once the mutation settles; never rejects (failures are in `error`). */
  setRating: (next: AiFeedbackRating) => Promise<void>;
  /** Explicit save action for the comment field. Resolves once the mutation settles. */
  submitComment: (value?: string) => Promise<void>;
  /** Clears the current mutation error and allows retry. */
  clearError: () => void;
  /** Retries the last failed action (rating or comment, whichever failed). */
  retry: () => void;
}

const AI_FEEDBACK_ENDPOINT = '/api/v1/ai-feedback';

export function useAiFeedback({
  outputType,
  outputId,
  aquariumId,
}: UseAiFeedbackArgs): UseAiFeedbackResult {
  const queryClient = useQueryClient();
  const queryKey = ['ai-feedback', outputType, outputId];
  const outputParams = { params: { outputType, outputId } };

  const [comment, setCommentState] = useState('');
  const [savedComment, setSavedComment] = useState('');
  const [lastFailedAction, setLastFailedAction] = useState<
    { type: 'rating'; value: Exclude<AiFeedbackRating, null> } | { type: 'comment' } | null
  >(null);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async (): Promise<AiFeedbackApiRecord | null> => {
      const response = await apiClient.get<AiFeedbackEnvelope>(AI_FEEDBACK_ENDPOINT, outputParams);
      return response?.data ?? null;
    },
  });

  useEffect(() => {
    if (data) {
      setCommentState(data.comment ?? '');
      setSavedComment(data.comment ?? '');
    }
  }, [data]);

  const rating = ratingFromApi(data?.rating);
  const hasRating = rating !== null;

  const upsertMutation = useMutation({
    // A payload saves the rating (+ comment); null removes the user's rating
    // (clicking the active thumb again — DELETE, 404 means already gone).
    mutationFn: async (payload: UpsertAiFeedbackPayload | null): Promise<AiFeedbackApiRecord | null> => {
      if (payload === null) {
        try {
          await apiClient.delete(AI_FEEDBACK_ENDPOINT, outputParams);
        } catch (err) {
          if (!(err instanceof ApiError && err.status === 404)) throw err;
        }
        return null;
      }
      const response = await apiClient.post<AiFeedbackEnvelope>(AI_FEEDBACK_ENDPOINT, payload);
      return response?.data ?? null;
    },
    onSuccess: (record) => {
      queryClient.setQueryData<AiFeedbackApiRecord | null>(queryKey, record);
      setCommentState(record?.comment ?? '');
      setSavedComment(record?.comment ?? '');
      setLastFailedAction(null);
    },
  });

  const setRating = useCallback(
    async (next: AiFeedbackRating): Promise<void> => {
      const nextRating: AiFeedbackRating = rating === next ? null : next;
      const submittedValue = (next ?? rating) as Exclude<AiFeedbackRating, null>;
      setLastFailedAction({ type: 'rating', value: submittedValue });
      try {
        await upsertMutation.mutateAsync(
          nextRating === null
            ? null
            : { aquariumId, outputType, outputId, rating: ratingToApi(nextRating), comment: comment || null },
        );
      } catch {
        // Surfaced through `error` / `isError`; callers fire-and-forget clicks.
      }
    },
    [rating, comment, aquariumId, outputType, outputId, upsertMutation],
  );

  const submitComment = useCallback(
    async (value?: string): Promise<void> => {
      if (!hasRating) {
        return;
      }
      setLastFailedAction({ type: 'comment' });
      try {
        await upsertMutation.mutateAsync({
          aquariumId,
          outputType,
          outputId,
          rating: ratingToApi(rating as Exclude<AiFeedbackRating, null>),
          comment: (value ?? comment) || null,
        });
      } catch {
        // Surfaced through `error` / `isError`.
      }
    },
    [hasRating, aquariumId, outputType, outputId, rating, comment, upsertMutation],
  );

  const clearError = useCallback(() => {
    upsertMutation.reset();
    setLastFailedAction(null);
  }, [upsertMutation]);

  const retry = useCallback(() => {
    if (!lastFailedAction) {
      return;
    }
    if (lastFailedAction.type === 'rating') {
      void setRating(lastFailedAction.value);
    } else {
      void submitComment();
    }
  }, [lastFailedAction, setRating, submitComment]);

  const setComment = useCallback((value: string) => {
    setCommentState(value);
  }, []);

  const isRatingSubmitting =
    upsertMutation.isPending && lastFailedAction?.type === 'rating';
  const isCommentSubmitting =
    upsertMutation.isPending && lastFailedAction?.type === 'comment';

  return {
    rating,
    comment,
    setComment,
    isLoading,
    isRatingSubmitting,
    isCommentSubmitting,
    isMutating: upsertMutation.isPending,
    isError: upsertMutation.isError,
    error: upsertMutation.isError ? (upsertMutation.error as Error) : null,
    hasRating,
    isCommentDirty: comment !== savedComment,
    setRating,
    submitComment,
    clearError,
    retry,
  };
}
